import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ExternalLink, ChevronRight, Users, FolderOpen, Loader, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchAkademikFaaliyet } from '../../utils/akademikFaaliyetCache';

const DEPT_COLORS = {
  'Matematik':          '#2563EB',
  'Biyoloji':           '#059669',
  'Kimya':              '#DC2626',
  'İstatistik':         '#7C3AED',
  'Aktüerya Bilimleri': '#D97706',
};

const Q_STYLES = {
  'q1': { bg: '#14532d', text: '#fff', label: 'Q1' },
  'q2': { bg: '#1e3a8a', text: '#fff', label: 'Q2' },
  'q3': { bg: '#78350f', text: '#fff', label: 'Q3' },
  'q4': { bg: '#374151', text: '#fff', label: 'Q4' },
};

const TUR_STYLES = {
  'SCI/SSCI':           { bg: '#dbeafe', text: '#1e40af' },
  'Ulusal':             { bg: '#dcfce7', text: '#166534' },
  'Uluslararasi Diger': { bg: '#ede9fe', text: '#5b21b6' },
  'ESCI':               { bg: '#ffedd5', text: '#9a3412' },
  'AHCI':               { bg: '#fef9c3', text: '#713f12' },
};

const getDeptColor = b => DEPT_COLORS[b] || '#7B0D1E';

const PublicationsSection = () => {
  const { t } = useTranslation();
  const [bolumler, setBolumler] = useState([]);
  const [makaleler, setMakaleler] = useState([]);
  const [projeler, setProjeler]   = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    fetchAkademikFaaliyet()
      .then(data => {
        setBolumler(data.bolumler);
        setMakaleler(data.makaleler);
        setProjeler(data.projeler);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Her bölümden bir örnek: bölümün en güncel Q1 makalesi (Q1'i yoksa en güncel makalesi).
  // En güncel olan öne çıkan kartta gösterilir.
  const recentPublications = useMemo(() => {
    const byNewest = (a, b) => (b.donem || 0) - (a.donem || 0);
    const isQ1 = m => (m.q_dilimi || '').toLowerCase() === 'q1';
    const deptKey = m => (m.bolum || '').trim().toLocaleLowerCase('tr');
    const withTitle = makaleler.filter(m => m.makale_adi && deptKey(m));
    const depts = [...new Set(withTitle.map(deptKey))];

    const picks = depts
      .map(key => {
        const own = withTitle.filter(m => deptKey(m) === key).sort(byNewest);
        return own.find(isQ1) || own[0];
      })
      .filter(Boolean)
      .sort((a, b) => (isQ1(b) - isQ1(a)) || byNewest(a, b));

    return picks.slice(0, 5);
  }, [makaleler]);

  const formatDOI = doi => doi ? (doi.startsWith('http') ? doi : `https://doi.org/${doi}`) : null;

  const stats = [
    { label: t('home.publications.statPublication'), value: makaleler.length, icon: BookOpen },
    { label: t('home.publications.statProject'),     value: projeler.length,  icon: FolderOpen },
    { label: t('home.publications.statDepartment'),  value: bolumler.length,  icon: Users },
  ];

  return (
    <section style={{ background: '#F8F6F1' }} className="relative overflow-hidden py-24">

      {/* Moleküler SVG arka plan */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none select-none" aria-hidden="true">
        <defs>
          <pattern id="pub-mol" x="0" y="0" width="90" height="90" patternUnits="userSpaceOnUse">
            <circle cx="45" cy="45" r="3"   fill="#7B0D1E" fillOpacity="0.18"/>
            <circle cx="0"  cy="0"  r="2"   fill="#7B0D1E" fillOpacity="0.14"/>
            <circle cx="90" cy="0"  r="2"   fill="#7B0D1E" fillOpacity="0.14"/>
            <circle cx="0"  cy="90" r="2"   fill="#7B0D1E" fillOpacity="0.14"/>
            <circle cx="90" cy="90" r="2"   fill="#7B0D1E" fillOpacity="0.14"/>
            <circle cx="45" cy="0"  r="1.5" fill="#7B0D1E" fillOpacity="0.10"/>
            <circle cx="0"  cy="45" r="1.5" fill="#7B0D1E" fillOpacity="0.10"/>
            <circle cx="90" cy="45" r="1.5" fill="#7B0D1E" fillOpacity="0.10"/>
            <circle cx="45" cy="90" r="1.5" fill="#7B0D1E" fillOpacity="0.10"/>
            <line x1="45" y1="45" x2="0"  y2="0"  stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.11"/>
            <line x1="45" y1="45" x2="90" y2="0"  stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.11"/>
            <line x1="45" y1="45" x2="0"  y2="90" stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.11"/>
            <line x1="45" y1="45" x2="90" y2="90" stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.11"/>
            <line x1="45" y1="45" x2="45" y2="0"  stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.08"/>
            <line x1="45" y1="45" x2="0"  y2="45" stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.08"/>
            <line x1="45" y1="45" x2="90" y2="45" stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.08"/>
            <line x1="45" y1="45" x2="45" y2="90" stroke="#7B0D1E" strokeWidth="0.7" strokeOpacity="0.08"/>
          </pattern>
          <radialGradient id="pub-fade" cx="50%" cy="50%" r="70%">
            <stop offset="0%"   stopColor="white" stopOpacity="0.15"/>
            <stop offset="100%" stopColor="white" stopOpacity="1"/>
          </radialGradient>
          <mask id="pub-mask">
            <rect width="100%" height="100%" fill="url(#pub-fade)"/>
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="url(#pub-mol)" mask="url(#pub-mask)"/>
      </svg>

      <div className="max-w-5xl mx-auto px-6 relative z-10">

        {/* ── Başlık ── */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px" style={{ background: '#7B0D1E' }} />
            <span className="font-display text-xs font-bold uppercase tracking-[0.22em]" style={{ color: '#7B0D1E' }}>
              Araştırma & Yayın
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-display font-extrabold tracking-tight text-gray-900" style={{ fontSize: 'clamp(1.65rem, 4.2vw, 2.6rem)', lineHeight: 1.15 }}>
              {t('home.publications.title')}<br />
              <span style={{ color: '#7B0D1E' }}>{t('home.publications.titleHighlight')}</span>
            </h2>
            <p className="text-gray-500 max-w-xs text-sm leading-relaxed md:text-right">
              {t('home.publications.subtitle')}
            </p>
          </div>
        </motion.div>

        {/* ── İstatistikler ── */}
        <motion.div
          className="grid grid-cols-3 mb-10 border border-gray-200 rounded-2xl overflow-hidden bg-white"
          style={{ boxShadow: '0 2px 20px rgba(0,0,0,0.05)' }}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse p-7 flex flex-col gap-3 border-r border-gray-100 last:border-0">
                <div className="h-10 bg-gray-100 rounded w-20" />
                <div className="h-3 bg-gray-50 rounded w-28" />
              </div>
            ))
          ) : stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label}
                   className={`p-3.5 sm:p-6 md:p-7 flex flex-col justify-center ${i < stats.length - 1 ? 'border-r border-gray-100' : ''}`}>
                <div className="flex items-start gap-3">
                  <Icon size={14} className="mt-1.5 flex-shrink-0 hidden sm:block" style={{ color: '#7B0D1E' }} />
                  <div>
                    <p className="font-display font-extrabold text-gray-900 leading-none mb-1.5 tracking-tight"
                       style={{ fontSize: 'clamp(1.35rem, 3.2vw, 2.1rem)' }}>
                      {s.value > 0 ? s.value.toLocaleString('tr-TR') : '—'}
                    </p>
                    <p className="font-display text-[10px] sm:text-[11px] text-gray-500 font-semibold uppercase tracking-wide">{s.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* ── Yayın Listesi ── */}
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <Loader className="animate-spin" size={24} style={{ color: '#7B0D1E' }} />
            <p className="text-gray-400 text-xs tracking-widest uppercase">{t('home.publications.loading')}</p>
          </div>
        ) : recentPublications.length > 0 && (
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* ── Büyük featured kart (ilk yayın) ── */}
            {recentPublications[0] && (() => {
              const pub       = recentPublications[0];
              const deptColor = getDeptColor(pub.bolum);
              const qKey      = (pub.q_dilimi || '').toLowerCase();
              const qStyle    = Q_STYLES[qKey];
              const turStyle  = TUR_STYLES[pub.makale_turu];
              return (
                <motion.div
                  className="bg-white rounded-2xl overflow-hidden flex transition-all duration-200 hover:shadow-lg"
                  style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Sol kolon — geniş */}
                  <div className="flex-shrink-0 flex flex-col items-center pt-5 sm:pt-7 pb-5 px-2.5 sm:px-4 gap-3"
                       style={{ borderRight: `3px solid ${deptColor}20`, background: deptColor + '09' }}>
                    <span className="font-display font-extrabold leading-none select-none text-lg sm:text-2xl"
                          style={{ color: deptColor + '90' }}>
                      01
                    </span>
                    <div className="flex-1 w-0.5 rounded-full min-h-[32px]" style={{ background: deptColor + '35' }} />
                  </div>

                  {/* İçerik — geniş padding */}
                  <div className="flex-1 min-w-0 p-4 sm:p-7">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {pub.bolum && (
                          <span className="font-display text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[0.12em] px-2 sm:px-2.5 py-1 rounded"
                                style={{ background: deptColor + '15', color: deptColor }}>
                            {pub.bolum}
                          </span>
                        )}
                        {turStyle && pub.makale_turu && (
                          <span className="font-display text-[10.5px] sm:text-[11px] font-semibold px-2 sm:px-2.5 py-1 rounded"
                                style={{ background: turStyle.bg, color: turStyle.text }}>
                            {pub.makale_turu}
                          </span>
                        )}
                      </div>
                      {qStyle && (
                        <span className="font-display flex-shrink-0 rounded-lg text-[12px] sm:text-[14px] px-2.5 sm:px-3.5 py-1"
                              style={{ background: qStyle.bg, color: qStyle.text, fontWeight: 800, letterSpacing: '0.08em', boxShadow: `0 2px 10px ${qStyle.bg}aa` }}>
                          {qStyle.label}
                        </span>
                      )}
                    </div>

                    <h3 className="font-display font-bold text-gray-900 leading-snug tracking-tight mb-3 text-[14.5px] sm:text-[19px] break-words [overflow-wrap:anywhere]">
                      {pub.makale_adi}
                    </h3>

                    <p className="text-[12.5px] sm:text-sm text-gray-600 mb-2 leading-relaxed">
                      <span className="font-semibold text-gray-800">{pub.sorumlu_yazar}</span>
                      {pub.yazarlar && pub.yazarlar !== pub.sorumlu_yazar && (
                        <span className="text-gray-500"> · {pub.yazarlar}</span>
                      )}
                    </p>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-3 border-t border-gray-100">
                      <span className="text-[12.5px] sm:text-sm italic text-gray-500 font-medium">{pub.dergi_adi}</span>
                      {pub.donem && <span className="text-xs text-gray-400">{pub.donem}</span>}
                      {pub.doi && (
                        <a href={formatDOI(pub.doi)} target="_blank" rel="noopener noreferrer"
                           className="ml-auto flex items-center gap-1 text-xs font-bold transition-opacity hover:opacity-60"
                           style={{ color: deptColor }}>
                          DOI <ArrowUpRight size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* ── Orta boy kartlar (diğer bölümlerin yayınları) — 2x2 grid ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recentPublications.slice(1).map((pub, index) => {
                const deptColor = getDeptColor(pub.bolum);
                const qKey      = (pub.q_dilimi || '').toLowerCase();
                const qStyle    = Q_STYLES[qKey];
                const turStyle  = TUR_STYLES[pub.makale_turu];
                return (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden flex transition-all duration-200 hover:shadow-md"
                    style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)' }}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.07 }}
                  >
                    {/* Sol kolon — dar */}
                    <div className="flex-shrink-0 flex flex-col items-center pt-4 pb-3 px-2.5 gap-2"
                         style={{ borderRight: `2px solid ${deptColor}15`, background: deptColor + '07' }}>
                      <span className="font-display font-extrabold leading-none select-none text-[15px]"
                            style={{ color: deptColor + '90' }}>
                        {String(index + 2).padStart(2, '0')}
                      </span>
                      <div className="flex-1 w-0.5 rounded-full min-h-[16px]" style={{ background: deptColor + '30' }} />
                    </div>

                    {/* İçerik — kompakt */}
                    <div className="flex-1 min-w-0 p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {pub.bolum && (
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                                  style={{ background: deptColor + '15', color: deptColor }}>
                              {pub.bolum}
                            </span>
                          )}
                          {turStyle && pub.makale_turu && (
                            <span className="font-display text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                  style={{ background: turStyle.bg, color: turStyle.text }}>
                              {pub.makale_turu}
                            </span>
                          )}
                        </div>
                        {qStyle && (
                          <span className="font-display flex-shrink-0 rounded-md text-[11px] px-2 py-0.5"
                                style={{ background: qStyle.bg, color: qStyle.text, fontWeight: 800, letterSpacing: '0.06em', boxShadow: `0 1px 6px ${qStyle.bg}88` }}>
                            {qStyle.label}
                          </span>
                        )}
                      </div>

                      <h3 className="font-display font-bold text-gray-900 leading-snug tracking-tight mb-2 text-[13.5px] sm:text-[14px] break-words [overflow-wrap:anywhere]">
                        {pub.makale_adi}
                      </h3>

                      <p className="text-xs text-gray-700 font-semibold mb-0.5 truncate">{pub.sorumlu_yazar}</p>
                      <p className="text-xs italic text-gray-500 truncate mb-3">{pub.dergi_adi}</p>

                      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
                        <span className="font-display text-[11px] font-semibold text-gray-400">{pub.donem}</span>
                        {pub.doi && (
                          <a href={formatDOI(pub.doi)} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-0.5 text-[11px] font-bold transition-opacity hover:opacity-60"
                             style={{ color: deptColor }}>
                            DOI <ArrowUpRight size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ── CTA ── */}
        <motion.div
          className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <p className="text-xs text-gray-400 tracking-wide">{t('home.publications.filterInfo')}</p>
          <Link
            to="/akademik-faaliyet"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all duration-200 hover:opacity-90"
            style={{ background: '#7B0D1E', color: '#fff' }}
          >
            {t('home.publications.viewAll')}
            <ChevronRight size={16} />
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default PublicationsSection;
